/**
 * 10.6C — AVISO DE DROP com canal PRÓPRIO.
 *
 * Antes todo item caído entrava no Banner central, que é ÚNICO: a mensagem de loot brigava
 * com missão, entrada de região, boss e dica de tutorial — o último a falar apagava o
 * anterior. Resultado: item lendário caía e o jogador não via, ou via meio segundo de um
 * texto miúdo no meio de outros. Agora o loot não usa mais o Banner:
 *
 *   · comum / incomum  → cartão pequeno numa coluna à esquerda (empilha até 4, some sozinho);
 *   · raro pra cima    → CARTÃO GRANDE no alto, com a cor da raridade, ícone grande, o nome
 *                        do item e os atributos que ele rolou. Fica 3 s, com brilho, e não
 *                        é derrubado por outra mensagem qualquer.
 *
 * Duplicata (shards / cópia de arma) também vem para cá, como nota discreta — antes ocupava
 * o Banner inteiro por 1,1 s.
 */
import { i18n } from "../i18n/i18n";
import { itemSvg } from "../items/icons";
import { itemName, attrLabel, formatAttrValue } from "../items/format";
import { rarityCss, rarityIndex } from "../items/Rarity";
import type { EquipmentInstance } from "../items/EquipmentTypes";

const FEED_MAX = 4;
const FEED_MS = 2600;
const HERO_MS = 3000;

export class LootToast {
  private readonly feed: HTMLDivElement;
  private readonly heroLayer: HTMLDivElement;
  private heroEl: HTMLDivElement | null = null;
  private heroTimer = 0;

  constructor(uiRoot: HTMLElement) {
    this.feed = document.createElement("div");
    this.feed.className = "loot-feed";
    uiRoot.appendChild(this.feed);
    this.heroLayer = document.createElement("div");
    this.heroLayer.className = "loot-hero-layer";
    uiRoot.appendChild(this.heroLayer);
  }

  /** Item novo entrou na bolsa. Raro+ ganha o cartão grande; o resto vai para a coluna. */
  drop(inst: EquipmentInstance): void {
    if (rarityIndex(inst.rarity) >= 2) this.hero(inst);
    else this.card(inst);
  }

  /** Nota discreta (duplicata virou shards, cópia de arma, bolsa cheia…). */
  note(text: string): void {
    const el = document.createElement("div");
    el.className = "loot-card loot-note";
    el.textContent = text;
    this.push(el);
  }

  private card(inst: EquipmentInstance): void {
    const c = rarityCss(inst.rarity);
    const el = document.createElement("div");
    el.className = "loot-card";
    el.style.setProperty("--rc", c);
    el.innerHTML = `<span class="lt-ic">${itemSvg(inst)}</span>
      <span class="lt-tx"><b>${escapeHtml(itemName(inst))}</b><i>${escapeHtml(i18n.t(`rarity.${inst.rarity}`))} · ${escapeHtml(i18n.t(`slot.${inst.slot}`))}</i></span>`;
    this.push(el);
  }

  private push(el: HTMLElement): void {
    this.feed.appendChild(el);
    while (this.feed.childElementCount > FEED_MAX) this.feed.firstElementChild?.remove();
    window.setTimeout(() => el.remove(), FEED_MS);
  }

  private hero(inst: EquipmentInstance): void {
    const c = rarityCss(inst.rarity);
    if (this.heroEl) {
      this.heroEl.remove();
      window.clearTimeout(this.heroTimer);
    }
    // 10.9II — 2 atributos, nao 3. Thiago: "a mensagem que aparece dos itens que caem, diminui".
    // A terceira linha empurrava o cartao pra cima da barra de armas em celular deitado, e o
    // cartao e uma CHAMADA ("caiu algo bom, olha a bolsa"), nao a ficha completa do item.
    const rolls = inst.rolls
      .slice(0, 2)
      .map((r) => `<span class="lh-attr">${escapeHtml(attrLabel(r.key))} <b>${escapeHtml(formatAttrValue(r.key, r.value))}</b></span>`)
      .join("");
    const el = document.createElement("div");
    el.className = "loot-hero";
    el.style.setProperty("--rc", c);
    el.innerHTML = `
      <div class="lh-ribbon">${escapeHtml(i18n.t(`rarity.${inst.rarity}`))}</div>
      <div class="lh-body">
        <span class="lh-ic">${itemSvg(inst)}</span>
        <span class="lh-tx">
          <b class="lh-name">${escapeHtml(itemName(inst))}</b>
          <span class="lh-sub">${escapeHtml(i18n.t(`slot.${inst.slot}`))} · L${inst.level}</span>
          <span class="lh-attrs">${rolls}</span>
        </span>
      </div>
      <i class="lh-shine"></i>`;
    this.heroLayer.appendChild(el);
    this.heroEl = el;
    // A coluna de itens comuns desce para baixo do cartão grande enquanto ele estiver na
    // tela. Medido de verdade (o cartão muda de altura conforme quantos atributos rolaram
    // e a largura do aparelho), então nunca sobrepõe — nem em tela estreita.
    this.pushFeedBelow(el);
    this.heroTimer = window.setTimeout(() => {
      el.classList.add("out");
      window.setTimeout(() => {
        el.remove();
        if (this.heroEl === el) {
          this.heroEl = null;
          this.feed.style.top = "";
        }
      }, 260);
    }, HERO_MS);
  }

  private pushFeedBelow(hero: HTMLElement): void {
    const b = hero.getBoundingClientRect().bottom;
    const css = getComputedStyle(this.feed);
    const current = parseFloat(css.top);
    if (!Number.isFinite(current) || b + 10 > current) this.feed.style.top = `${Math.round(b + 10)}px`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => (ch === "&" ? "&amp;" : ch === "<" ? "&lt;" : ch === ">" ? "&gt;" : "&quot;"));
}
